import React, { useState, useEffect } from 'react';
import { PreviewProps } from './types';
import { useTheme } from '../../context/ThemeContext';

/**
 * Live preview component showing how the color scheme looks
 */
export const ColorSchemePreview: React.FC<PreviewProps> = ({ scheme, mode }) => {
  const [originalColors, setOriginalColors] = useState<Record<string, string>>({});
  
  // Apply the color scheme to the preview area
  useEffect(() => {
    const root = document.documentElement;
    const colors = scheme.colors[mode];
    
    // Store original colors for restoration
    const original: Record<string, string> = {};
    Object.keys(colors).forEach(varName => {
      const currentValue = getComputedStyle(root).getPropertyValue(varName);
      if (currentValue) {
        original[varName] = currentValue.trim();
      }
    });
    setOriginalColors(original);
    
    // Apply new colors
    Object.entries(colors).forEach(([varName, value]) => {
      root.style.setProperty(varName, value);
    });
    
    // Cleanup function to restore original colors
    return () => {
      Object.entries(original).forEach(([varName, value]) => {
        root.style.setProperty(varName, value);
      });
    };
  }, [scheme, mode]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">
          Live Preview - {mode === 'light' ? 'Light' : 'Dark'} Mode
        </h3>
        <span className="text-sm text-foreground-muted">
          {scheme.name}
        </span>
      </div>
      
      {/* Preview Components */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Cards Preview */}
        <div className="card-container">
          <h4 className="form-section-title">Sample Card</h4>
          <div className="form-group">
            <label className="form-label">Sample Input</label>
            <input 
              type="text" 
              className="form-input" 
              placeholder="Type something..."
              defaultValue="Sample text"
            />
          </div>
          <div className="flex space-x-2 mt-4">
            <button className="auth-button-primary">Primary Button</button>
            <button className="auth-button-secondary">Secondary</button>
          </div>
        </div>
        
        {/* Status Colors Preview */}
        <div className="space-y-3">
          <div className="auth-message auth-message-success">
            <strong>Success:</strong> Operation completed successfully!
          </div>
          <div className="auth-message auth-message-warning">
            <strong>Warning:</strong> Please review your settings.
          </div>
          <div className="auth-message auth-message-error">
            <strong>Error:</strong> Something went wrong.
          </div>
          <div className="auth-message auth-message-info">
            <strong>Info:</strong> Here's some helpful information.
          </div>
        </div>
        
        {/* Form Elements Preview */}
        <div className="card-container">
          <h4 className="form-section-title">Form Elements</h4>
          <div className="form-group">
            <label className="form-label form-label-required">Required Field</label>
            <input type="text" className="form-input" placeholder="Required input" />
          </div>
          <div className="form-group">
            <label className="form-label">Select Option</label>
            <select className="form-select">
              <option>Option 1</option>
              <option>Option 2</option>
            </select>
          </div>
          <div className="form-checkbox-label">
            <input type="checkbox" className="form-checkbox" defaultChecked />
            <span className="form-checkbox-label">I agree to the terms</span>
          </div>
        </div>
        
        {/* Navigation Preview */}
        <div className="card-container">
          <h4 className="form-section-title">Navigation Elements</h4>
          <div className="auth-tab-container">
            <button className="auth-tab auth-tab-active">Active Tab</button>
            <button className="auth-tab">Inactive Tab</button>
            <button className="auth-tab">Another Tab</button>
          </div>
          <div className="mt-4 space-y-2">
            <div className="text-foreground">Primary text content</div>
            <div className="text-foreground-muted">Muted secondary text</div>
            <div className="text-accent">Accent colored text</div>
          </div>
        </div>
      </div>
      
      {/* Color Swatches */}
      <div className="card-container">
        <h4 className="form-section-title">Color Swatches</h4>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
          {Object.entries(scheme.colors[mode]).map(([varName, hslValue]) => (
            <div key={varName} className="text-center">
              <div 
                className="w-12 h-12 rounded border border-border mx-auto mb-1"
                style={{ backgroundColor: `hsl(${hslValue})` }}
                title={`${varName}: ${hslValue}`}
              />
              <div className="text-xs text-foreground-muted truncate">
                {varName.replace('--', '').replace('-', ' ')}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
